import { Component } from '@theme/component';

let resolvedSectionId = null;

function getSectionId() {
  if (resolvedSectionId) return resolvedSectionId;
  const sentinel = document.querySelector('[data-cart-drawer-recs-sentinel]');
  resolvedSectionId = sentinel?.dataset.sectionId ?? null;
  return resolvedSectionId;
}

class CartDrawerRecommendations extends Component {
  #productId = null;
  #hasLoaded = false;

  #onCartUpdate = (event) => {
    const { resource } = event.detail;
    const addedProductId = event.detail.data?.productId?.toString();
    const newProductId = addedProductId || resource?.items?.[0]?.product_id?.toString();
    const itemCount = resource?.item_count ?? event.detail.data?.itemCount ?? 1;

    if (itemCount === 0) {
      this.hidden = true;
      return;
    }

    this.hidden = false;

    if (newProductId && newProductId !== this.#productId) {
      this.#productId = newProductId;
      this.#load(newProductId);
    }
  };

  connectedCallback() {
    super.connectedCallback();
    // On re-injection after morphSection, avoid re-fetching if already loaded
    const isReinsertion = this.#hasLoaded;
    if (!this.#productId) {
      this.#productId = this.dataset.productId;
    }
    this.#moveToScrollContainer();
    if (this.#productId && !isReinsertion) this.#load(this.#productId);
    document.addEventListener('cart:update', this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('cart:update', this.#onCartUpdate);
    // morphSection just replaced cart-drawer__inner — re-inject on next frame.
    // Guard: a full-mode morph may have already inserted a fresh instance from
    // the server HTML and moved it into inner; if so, skip re-injection to
    // avoid ending up with two recommendation carousels.
    requestAnimationFrame(() => {
      const inner = document.querySelector('.cart-drawer__inner');
      if (inner?.querySelector('cart-drawer-recommendations')) return;
      this.#moveToScrollContainer();
    });
  }

  // Moves this element to the bottom of cart-drawer__inner so it sits
  // at the end of the cart's scroll area rather than outside it.
  #moveToScrollContainer() {
    const inner = document.querySelector('.cart-drawer__inner');
    if (inner && !inner.contains(this)) {
      inner.appendChild(this);
    }
  }

  async #load(productId) {
    const sectionId = getSectionId();
    if (!sectionId) {
      this.hidden = true;
      return;
    }
    const limit = this.dataset.limit || 10;
    try {
      const url = `/recommendations/products?section_id=${sectionId}&product_id=${productId}&limit=${limit}&intent=related`;
      const res = await fetch(url);
      if (!res.ok) {
        console.warn(`[cart-drawer-recommendations] fetch failed: ${res.status} ${res.statusText} — ${url}`);
        this.hidden = true;
        return;
      }
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const list = doc.querySelector('.cart-drawer-rec-list');
      if (!list || !list.children.length) {
        console.warn('[cart-drawer-recommendations] no .cart-drawer-rec-list found in response', doc.body.innerHTML.slice(0, 200));
        this.hidden = true;
        return;
      }

      // Inject any section-scoped stylesheets included in the response
      doc.querySelectorAll('style[data-section-stylesheet]').forEach((style) => {
        const key = style.dataset.resource || style.textContent.slice(0, 40);
        if (!document.querySelector(`style[data-injected-key="${CSS.escape(key)}"]`)) {
          const clone = style.cloneNode(true);
          clone.dataset.injectedKey = key;
          document.head.appendChild(clone);
        }
      });

      const container = this.querySelector('.cart-drawer-recommendations__items, .cart-drawer-rec-list');
      if (container) container.replaceWith(list);
      this.#hasLoaded = true;
      this.#bindArrows(list);
    } catch {
      this.hidden = true;
    }
  }

  #bindArrows(list) {
    const prevBtn = this.querySelector('.cart-drawer-recommendations__arrow--prev');
    const nextBtn = this.querySelector('.cart-drawer-recommendations__arrow--next');
    if (!prevBtn || !nextBtn) return;

    const update = () => {
      prevBtn.hidden = list.scrollLeft <= 0;
      nextBtn.hidden = list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;
    };

    const scrollBy = (direction) => {
      const card = list.querySelector(':scope > *');
      const amount = card ? card.offsetWidth + 12 : list.clientWidth / 2;
      list.scrollBy({ left: direction * amount, behavior: 'smooth' });
    };

    prevBtn.addEventListener('click', () => scrollBy(-1));
    nextBtn.addEventListener('click', () => scrollBy(1));
    list.addEventListener('scroll', update, { passive: true });
    new ResizeObserver(update).observe(list);
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
